import React, { useCallback, use, useEffect, useMemo } from "react";

import { Button, Input, Spinner } from "@fluentui/react-components";
import { Search20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { observer } from "mobx-react";

import { useDebounce } from "Shared/Hooks/Util/useDebounce";

import { M365PulseSearchboxInputProps } from "./M365PulseSearchBox.types";
import { SearchContext } from "./SearchStore";

export const CLEAR_BUTTON_WIDTH = 45;

const M365PulseSearchboxInputBase = React.forwardRef(
    (
        {
            disabled,
            "aria-label": ariaLabel = "Search",
            className,
            placeholder,
            onSearchChange,
            appearance,
            searchingStatus = "success",
            onFocus,
            onValueChange,
            value,
            clearable,
            customOnKeyDown: customOnKeyDown,
            "aria-controls": ariaControls,
            "aria-expanded": ariaExpanded,
            "aria-haspopup": ariaHasPopup,
            "aria-activedescendant": ariaActiveDescendant,
            onTabToResults,
            activeIndex,
            setActiveIndex,
            resultCount,
            onSelectIndex
        }: M365PulseSearchboxInputProps,
        ref: React.ForwardedRef<HTMLInputElement>
    ) => {
        const store = use(SearchContext);

        const inputValue = value !== undefined ? value : store?.keyword || "";

        const debouncedKeyword = useDebounce(inputValue);

        const onInputChange = useCallback(
            (ev: React.ChangeEvent<HTMLInputElement>) => {
                store?.setKeyword(ev.target.value);
                onValueChange?.(ev.target.value);
            },
            [store, onValueChange]
        );

        const firstItem = useMemo(() => {
            const results = store?.getEnhancedSearchResults();
            return results && results.length > 0 ? results[0] : undefined;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [store?.searchResultList]);

        const onKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                // If there's a custom handler, call it first
                if (customOnKeyDown) {
                    customOnKeyDown(e);
                    // If the custom handler didn't prevent default, continue with default handling
                    if (e.defaultPrevented) {
                        return;
                    }
                }

                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveIndex(Math.min(activeIndex + 1, resultCount - 1));
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveIndex(Math.max(activeIndex - 1, 0));
                } else if (e.key === "Enter" && activeIndex >= 0) {
                    e.preventDefault();
                    onSelectIndex(activeIndex);
                } else if (e.key === "Enter" && firstItem) {
                    firstItem.onClick();
                } else if (e.key === "Tab" && ariaExpanded && onTabToResults && !e.shiftKey) {
                    e.preventDefault();
                    onTabToResults();
                } else if (e.key === "Escape" && ariaExpanded) {
                    e.preventDefault();
                    e.stopPropagation();
                    store?.setOpen(false);
                    // Return focus to input
                    if (ref && "current" in ref && ref.current) {
                        ref.current.focus();
                    }
                }
            },
            [customOnKeyDown, firstItem, ariaExpanded, onTabToResults, activeIndex, setActiveIndex, resultCount, onSelectIndex, store, ref]
        );

        const clearSearch = useCallback(
            (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                store?.setKeyword("");
                onValueChange?.("");
                onSearchChange("");
            },
            [store, onValueChange, onSearchChange]
        );

        const clearButton =
            inputValue.length > 0 ? (
                <Button appearance="transparent" size="small" icon={<Dismiss20Regular />} onClick={clearSearch} aria-label="Clear search" disabled={disabled} />
            ) : null;

        useEffect(() => {
            onSearchChange(debouncedKeyword);
        }, [debouncedKeyword, onSearchChange]);

        const contentBefore = useMemo(
            () => (searchingStatus === "pending" ? <Spinner size="tiny" aria-label="searching status spinner" /> : <Search20Regular />),
            [searchingStatus]
        );

        return (
            <Input
                ref={ref}
                className={className}
                appearance={appearance}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
                value={inputValue}
                onChange={onInputChange}
                onFocus={onFocus}
                disabled={disabled}
                aria-label={ariaLabel}
                role="combobox"
                aria-controls={ariaControls}
                aria-expanded={ariaExpanded}
                aria-haspopup={ariaHasPopup}
                aria-activedescendant={ariaActiveDescendant}
                autoComplete="off"
                contentBefore={contentBefore}
                contentAfter={clearable ? clearButton : null}
                input={{ role: "combobox" }}
            />
        );
    }
);

export const M365PulseSearchboxInput = observer(M365PulseSearchboxInputBase);
